"use client";

import { Component, type ReactNode } from "react";

interface Props {
	children: ReactNode;
	/** Rendered instead of the children when a child throws. Defaults to null. */
	fallback?: ReactNode;
}

interface State {
	hasError: boolean;
}

/**
 * Minimal error boundary for non-critical UI. If a child throws during render,
 * it shows `fallback` (nothing by default) instead of letting the error bubble
 * up and take down the surrounding page. Only catches render/lifecycle errors —
 * async errors (e.g. React Query) surface via their own state, not here.
 */
export class ErrorBoundary extends Component<Props, State> {
	state: State = { hasError: false };

	static getDerivedStateFromError(): State {
		return { hasError: true };
	}

	componentDidCatch(error: unknown) {
		console.error("ErrorBoundary caught an error:", error);
	}

	render() {
		if (this.state.hasError) return this.props.fallback ?? null;
		return this.props.children;
	}
}
